import { memo, useMemo, useState } from 'react';
import { Copy, Check, Paperclip } from 'lucide-react';
import ReactMarkdown, { type Components } from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import type { ChatMessage, FileAttachment } from '@/lib/types';
import { cn } from '@/lib/utils';
import { ArtifactViewerModal } from '@/components/workflows/ArtifactViewerModal';
import { Mermaid, isMermaidCode, preChildIsMermaid } from '@/components/markdown/Mermaid';
import { ActionSuggestionCard } from './ActionSuggestionCard';
import { parseActionSuggestions, type ActionSuggestion } from '@/lib/parseActionSuggestions';
import { stripSuggestedRepliesMarker } from '@/lib/parseSuggestedReplies';

// Hoisted to module scope to prevent new references on every render
const REMARK_PLUGINS = [remarkGfm, remarkBreaks];
const REHYPE_PLUGINS = [rehypeHighlight];

// Matches outcome IDs like O-WBM-004, O-ARCHON-001
const OUTCOME_ID_RE = /^O-[A-Z]{2,8}-\d{3}$/;

// Matches artifact paths (forward- and back-slash safe); groups: [1] runId, [2] filename
const ARTIFACT_PATH_RE = /artifacts[/\\]runs[/\\]([a-fA-F0-9-]+)[/\\](.+)/;

function extractArtifactInfo(text: string): { runId: string; filename: string } | null {
  const match = ARTIFACT_PATH_RE.exec(text);
  if (!match) return null;
  const filename = match[2].replace(/\\/g, '/');
  if (filename.split('/').some(s => s === '..')) return null;
  return {
    runId: match[1],
    filename,
  };
}

function makeMarkdownComponents(
  onArtifactClick: (runId: string, filename: string) => void,
  onOutcomeClick?: (outcomeId: string) => void
): Components {
  return {
    pre: ({ children, ...props }: React.ComponentPropsWithoutRef<'pre'>): React.ReactElement => {
      // Mermaid renders block-level SVG; let it escape the monospace <pre> wrapper.
      if (preChildIsMermaid(children)) {
        return <>{children}</>;
      }
      return (
        <pre
          className="overflow-x-auto rounded-lg border border-border bg-surface p-4 font-mono text-sm"
          {...props}
        >
          {children}
        </pre>
      );
    },
    code: ({
      children,
      className,
      ...props
    }: React.ComponentPropsWithoutRef<'code'> & { className?: string }): React.ReactElement => {
      // The string guard handles the (currently unreachable) case where a syntax
      // highlighter has tokenized the block into elements; a mermaid block would
      // then visibly fall through to a code block rather than render as a diagram.
      if (isMermaidCode(className) && typeof children === 'string') {
        return <Mermaid chart={children} />;
      }
      const isBlock = className?.startsWith('language-') || className?.startsWith('hljs');
      if (isBlock) {
        return (
          <code className={cn(className, 'font-mono')} {...props}>
            {children}
          </code>
        );
      }
      if (typeof children === 'string') {
        const artifact = extractArtifactInfo(children);
        if (artifact) {
          const { runId, filename } = artifact;
          const displayName = filename.split('/').pop() ?? filename;
          if (filename.endsWith('.md')) {
            return (
              <button
                type="button"
                className="cursor-pointer rounded bg-background px-1.5 py-0.5 font-mono text-sm text-accent-bright hover:text-primary transition-colors"
                onClick={(): void => {
                  onArtifactClick(runId, filename);
                }}
              >
                {displayName}
              </button>
            );
          }
          const encodedFilename = filename.split('/').map(encodeURIComponent).join('/');
          return (
            <a
              href={`/api/artifacts/${encodeURIComponent(runId)}/${encodedFilename}`}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded bg-background px-1.5 py-0.5 font-mono text-sm text-accent-bright underline decoration-accent-bright/40 hover:decoration-accent-bright"
            >
              {displayName}
            </a>
          );
        }
        // Clickable outcome IDs — render as navigable pills
        const trimmed = children.trim();
        if (OUTCOME_ID_RE.test(trimmed) && onOutcomeClick) {
          return (
            <button
              type="button"
              className="cursor-pointer rounded-full bg-primary/10 px-2 py-0.5 font-mono text-sm text-primary hover:bg-primary/20 transition-colors"
              onClick={(): void => {
                onOutcomeClick(trimmed);
              }}
            >
              {trimmed}
            </button>
          );
        }
      }
      return (
        <code
          className="rounded bg-background px-1.5 py-0.5 font-mono text-sm text-accent-bright"
          {...props}
        >
          {children}
        </code>
      );
    },
    table: ({
      children,
      ...props
    }: React.ComponentPropsWithoutRef<'table'>): React.ReactElement => (
      <div className="overflow-x-auto">
        <table {...props}>{children}</table>
      </div>
    ),
    blockquote: ({
      children,
      ...props
    }: React.ComponentPropsWithoutRef<'blockquote'>): React.ReactElement => (
      <blockquote className="border-l-2 border-primary pl-4 text-text-secondary" {...props}>
        {children}
      </blockquote>
    ),
    a: ({ children, ...props }: React.ComponentPropsWithoutRef<'a'>): React.ReactElement => (
      <a
        className="text-primary underline decoration-primary/40 hover:decoration-primary"
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      >
        {children}
      </a>
    ),
  };
}

/** Detect if a string is a complete JSON object/array */
function isJsonString(str: string): boolean {
  const trimmed = str.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return false;
  try {
    JSON.parse(trimmed);
    return true;
  } catch {
    return false;
  }
}

interface MessageBubbleProps {
  message: ChatMessage;
  onOutcomeClick?: (outcomeId: string) => void;
  onActionExecute?: (action: ActionSuggestion) => void;
}

function MessageBubbleRaw({
  message,
  onOutcomeClick,
  onActionExecute,
}: MessageBubbleProps): React.ReactElement {
  const isUser = message.role === 'user';
  const isThinking = message.isStreaming && !message.content;
  const [copied, setCopied] = useState(false);
  const [artifactViewer, setArtifactViewer] = useState<{ runId: string; filename: string } | null>(
    null
  );
  // Strip suggested-reply markers so they never render in — or get copied from — the message.
  // JSON payloads pass through untouched: the marker only appears in prose turns, and stripping
  // could corrupt a JSON blob that legitimately contains the literal marker text.
  const displayContent = useMemo(
    () =>
      isJsonString(message.content)
        ? message.content
        : stripSuggestedRepliesMarker(message.content),
    [message.content]
  );
  // Parse action suggestions from completed assistant messages (skip during streaming)
  const suggestions = useMemo(
    () =>
      !isUser && !message.isStreaming && displayContent
        ? parseActionSuggestions(displayContent)
        : [],
    [isUser, message.isStreaming, displayContent]
  );

  // setArtifactViewer is a stable React state setter — empty dep array is intentional
  // onOutcomeClick is excluded from deps because it's a stable callback from parent
  const markdownComponents = useMemo(
    () =>
      makeMarkdownComponents((runId, filename) => {
        setArtifactViewer({ runId, filename });
      }, onOutcomeClick),
    [onOutcomeClick]
  );

  const copyMessage = (): void => {
    void navigator.clipboard.writeText(displayContent).then(
      () => {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 1500);
      },
      (err: unknown) => {
        console.error('[Chat] Clipboard write failed', {
          error: err instanceof Error ? err.message : err,
        });
      }
    );
  };

  return (
    <>
      <div className={cn('group flex w-full', isUser ? 'justify-end' : 'justify-start')}>
        <div
          className={cn(
            'relative',
            isUser
              ? 'max-w-[70%] rounded-2xl rounded-br-sm bg-accent-muted px-4 py-2.5'
              : 'max-w-full rounded-lg border-l-2 border-primary/30 pl-4'
          )}
        >
          {isUser ? (
            <div className="flex flex-col gap-1.5">
              <div className="flex items-start gap-2">
                <p className="text-sm text-text-primary whitespace-pre-wrap flex-1">
                  {message.content}
                </p>
                <button
                  onClick={copyMessage}
                  className="shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity text-text-tertiary hover:text-text-primary"
                  title="Copy message"
                  aria-label={copied ? 'Copied' : 'Copy message'}
                >
                  {copied ? (
                    <Check className="h-3.5 w-3.5 text-success" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                </button>
              </div>
              {message.files && message.files.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {message.files.map((file: FileAttachment) => (
                    <div
                      key={file.id}
                      className="flex items-center gap-1 rounded-md bg-surface-inset px-1.5 py-0.5 text-xs text-text-secondary"
                      title={file.name}
                    >
                      <Paperclip className="h-3 w-3 shrink-0" />
                      <span className="max-w-[120px] truncate">{file.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="chat-markdown max-w-none text-sm text-text-primary">
              {isThinking && (
                <div className="flex items-center gap-1.5 py-1">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-text-tertiary" />
                  <span
                    className="h-1.5 w-1.5 animate-pulse rounded-full bg-text-tertiary"
                    style={{ animationDelay: '0.2s' }}
                  />
                  <span
                    className="h-1.5 w-1.5 animate-pulse rounded-full bg-text-tertiary"
                    style={{ animationDelay: '0.4s' }}
                  />
                </div>
              )}
              {isJsonString(displayContent) ? (
                <details className="group">
                  <summary className="cursor-pointer text-sm text-text-secondary hover:text-text-primary">
                    <span className="text-xs bg-surface-elevated rounded px-1.5 py-0.5 font-mono">
                      JSON output
                    </span>
                  </summary>
                  <pre className="mt-2 text-xs bg-surface-inset rounded p-3 overflow-x-auto">
                    {JSON.stringify(JSON.parse(displayContent.trim()) as unknown, null, 2)}
                  </pre>
                </details>
              ) : (
                <ReactMarkdown
                  remarkPlugins={REMARK_PLUGINS}
                  rehypePlugins={REHYPE_PLUGINS}
                  components={markdownComponents}
                >
                  {displayContent}
                </ReactMarkdown>
              )}
              {/* Action suggestion cards — only on completed assistant messages */}
              {!isUser && !message.isStreaming && onActionExecute && suggestions.length > 0 && (
                <div className="mt-2 flex flex-col gap-1.5">
                  {suggestions.map((action, i) => (
                    <ActionSuggestionCard
                      key={`${action.type}-${String(i)}`}
                      action={action}
                      onExecute={onActionExecute}
                    />
                  ))}
                </div>
              )}
              {message.isStreaming && message.content && (
                <span className="inline-block h-4 w-0.5 animate-pulse bg-primary align-text-bottom" />
              )}
            </div>
          )}

          {!isThinking && (
            <div className="mt-0.5 text-[10px] text-text-tertiary">
              {new Date(message.timestamp).toLocaleTimeString()}
            </div>
          )}
        </div>
      </div>
      {artifactViewer && (
        <ArtifactViewerModal
          open={true}
          onOpenChange={() => {
            setArtifactViewer(null);
          }}
          runId={artifactViewer.runId}
          filename={artifactViewer.filename}
        />
      )}
    </>
  );
}

// Memoize: only re-render when message content/state actually changes
const messageBubble = memo(MessageBubbleRaw, (prev, next) => {
  return (
    prev.message.content === next.message.content &&
    prev.message.isStreaming === next.message.isStreaming &&
    prev.message.toolCalls === next.message.toolCalls &&
    prev.message.error === next.message.error &&
    prev.message.workflowDispatch === next.message.workflowDispatch &&
    prev.message.workflowResult === next.message.workflowResult &&
    prev.message.files === next.message.files
  );
});

export { messageBubble as MessageBubble };
